import type { AudioQualityAnalyticsResponse } from './audioQuality.types';
import {
  mapAudioQualityAnalytics,
  mapBreakdownItem,
  mapComparisonDetail,
  mapComparisonPage,
  mapMetrics,
  mapRegeneration,
  mapTimelineItem,
  mapVerificationItem,
} from './mappers';
import type {
  BreakdownDimension,
  BreakdownItemResponse,
  BreakdownRow,
  ComparisonDetail,
  ComparisonDetailResponse,
  ComparisonListResponse,
  ComparisonPage,
  ItemsResponse,
  Regeneration,
  RegenerationResponse,
  SummaryMetrics,
  SummaryMetricsResponse,
  SummaryQualityFilters,
  TimelineBucket,
  TimelineItemResponse,
  TimelinePoint,
  VerificationDimension,
  VerificationItemResponse,
  VerificationRow,
} from './types';

export const SUMMARY_QUALITY_ENDPOINTS = {
  metrics: '/api/admin/summary-quality/metrics',
  timeline: '/api/admin/summary-quality/timeline',
  breakdown: '/api/admin/summary-quality/breakdown',
  regeneration: '/api/admin/summary-quality/regeneration',
  verification: '/api/admin/summary-quality/verification',
  comparisons: '/api/admin/summary-comparisons',
  audio: '/api/admin/summary-quality/audio',
} as const;

export const DEFAULT_RANGE_DAYS = 90;
export const COMPARISONS_PAGE_SIZE = 20;
export const BREAKDOWN_LIMIT = 200;
export const FILTER_OPTIONS_PAGE_SIZE = 200;
export const FILTER_OPTIONS_MAX_GROUPS = 1000;

type QueryValue = string | number | boolean | null | undefined;
type RequestOptions = { signal?: AbortSignal };

export class SummaryQualityApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SummaryQualityApiError';
    this.status = status;
  }
}

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

export const getDefaultRange = (now = new Date()): Pick<SummaryQualityFilters, 'from' | 'to'> => {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const from = new Date(today);
  from.setUTCDate(today.getUTCDate() - DEFAULT_RANGE_DAYS + 1);
  return { from: toDateInputValue(from), to: toDateInputValue(today) };
};

export const buildSummaryQualityParams = (filters: SummaryQualityFilters, extra: Record<string, QueryValue> = {}) => {
  const entries: Record<string, QueryValue> = {
    from: filters.from,
    to: filters.to,
    org_id: filters.orgId,
    account_id: filters.accountId,
    model: filters.model,
    prompt_hash: filters.promptHash,
    mode: filters.mode,
    template_id: filters.templateId,
    include_backfilled: filters.includeBackfilled,
    ...extra,
  };
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    if (value != null && value !== '') params.set(key, String(value));
  }
  return params;
};

export const buildAudioQualityParams = (
  filters: Pick<SummaryQualityFilters, 'from' | 'to' | 'orgId' | 'mode'>,
  bucket: TimelineBucket,
  scorerVersion: string | null
) => {
  const params = new URLSearchParams({ from: filters.from, to: filters.to, bucket });
  if (filters.orgId) params.set('org_id', filters.orgId);
  if (filters.mode) params.set('mode', filters.mode);
  if (scorerVersion) params.set('scorer_version', scorerVersion);
  return params;
};

const readErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    return payload.error || payload.detail || payload.message || `Request failed: ${response.status}`;
  } catch {
    return `Request failed: ${response.status}`;
  }
};

export const fetchAdminJson = async <T>(path: string, params: URLSearchParams, options: RequestOptions = {}) => {
  const query = params.toString();
  const response = await fetch(query ? `${path}?${query}` : path, {
    credentials: 'include',
    signal: options.signal,
  });
  if (!response.ok) {
    throw new SummaryQualityApiError(await readErrorMessage(response), response.status);
  }
  return (await response.json()) as T;
};

export const fetchMetrics = async (filters: SummaryQualityFilters, options?: RequestOptions): Promise<SummaryMetrics> =>
  mapMetrics(
    await fetchAdminJson<SummaryMetricsResponse>(
      SUMMARY_QUALITY_ENDPOINTS.metrics,
      buildSummaryQualityParams(filters),
      options
    )
  );

export const fetchAudioQuality = async (
  filters: Pick<SummaryQualityFilters, 'from' | 'to' | 'orgId' | 'mode'>,
  bucket: TimelineBucket,
  scorerVersion: string | null,
  options?: RequestOptions
) =>
  mapAudioQualityAnalytics(
    await fetchAdminJson<AudioQualityAnalyticsResponse>(
      SUMMARY_QUALITY_ENDPOINTS.audio,
      buildAudioQualityParams(filters, bucket, scorerVersion),
      options
    )
  );

export const fetchTimeline = async (
  filters: SummaryQualityFilters,
  bucket: TimelineBucket,
  options?: RequestOptions
): Promise<TimelinePoint[]> => {
  const response = await fetchAdminJson<ItemsResponse<TimelineItemResponse>>(
    SUMMARY_QUALITY_ENDPOINTS.timeline,
    buildSummaryQualityParams(filters, { bucket }),
    options
  );
  return response.items.map(mapTimelineItem);
};

export const fetchBreakdown = async (
  filters: SummaryQualityFilters,
  by: BreakdownDimension,
  options?: RequestOptions
): Promise<BreakdownRow[]> => {
  const response = await fetchAdminJson<ItemsResponse<BreakdownItemResponse>>(
    SUMMARY_QUALITY_ENDPOINTS.breakdown,
    buildSummaryQualityParams(filters, { by, limit: BREAKDOWN_LIMIT }),
    options
  );
  return response.items.map(mapBreakdownItem);
};

/**
 * Every group for a dropdown's options, paged by offset past the single-page
 * `BREAKDOWN_LIMIT` a tab's breakdown table uses, up to `FILTER_OPTIONS_MAX_GROUPS`
 * total (stops there even if more remain upstream).
 */
export const fetchAllBreakdownOptions = async (
  filters: SummaryQualityFilters,
  by: BreakdownDimension,
  options?: RequestOptions
): Promise<BreakdownRow[]> => {
  const rows: BreakdownRow[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  while (rows.length < total && rows.length < FILTER_OPTIONS_MAX_GROUPS) {
    const response = await fetchAdminJson<ItemsResponse<BreakdownItemResponse>>(
      SUMMARY_QUALITY_ENDPOINTS.breakdown,
      buildSummaryQualityParams(filters, { by, limit: FILTER_OPTIONS_PAGE_SIZE, offset }),
      options
    );
    if (response.items.length === 0) break;
    rows.push(...response.items.map(mapBreakdownItem));
    total = response.total ?? rows.length;
    offset += response.items.length;
  }
  return rows.slice(0, FILTER_OPTIONS_MAX_GROUPS);
};

export const fetchRegeneration = async (
  filters: SummaryQualityFilters,
  options?: RequestOptions
): Promise<Regeneration> =>
  mapRegeneration(
    await fetchAdminJson<RegenerationResponse>(
      SUMMARY_QUALITY_ENDPOINTS.regeneration,
      buildSummaryQualityParams(filters),
      options
    )
  );

export const fetchVerification = async (
  filters: SummaryQualityFilters,
  by: VerificationDimension,
  options?: RequestOptions
): Promise<VerificationRow[]> => {
  const response = await fetchAdminJson<ItemsResponse<VerificationItemResponse>>(
    SUMMARY_QUALITY_ENDPOINTS.verification,
    buildSummaryQualityParams(filters, { by }),
    options
  );
  return response.items.map(mapVerificationItem);
};

export const fetchComparisonPage = async (
  page: number,
  dateRange: Pick<SummaryQualityFilters, 'from' | 'to'>,
  options?: RequestOptions
): Promise<ComparisonPage> => {
  // The list has no upstream support for org/user/model filters, only the date range.
  const params = new URLSearchParams({
    page: String(page),
    limit: String(COMPARISONS_PAGE_SIZE),
    has_both: 'true',
    start_date: dateRange.from,
    end_date: dateRange.to,
  });
  return mapComparisonPage(
    await fetchAdminJson<ComparisonListResponse>(SUMMARY_QUALITY_ENDPOINTS.comparisons, params, options)
  );
};

export const fetchComparisonDetail = async (audioId: number, options?: RequestOptions): Promise<ComparisonDetail> =>
  mapComparisonDetail(
    await fetchAdminJson<ComparisonDetailResponse>(
      `${SUMMARY_QUALITY_ENDPOINTS.comparisons}/${audioId}/detailed`,
      new URLSearchParams(),
      options
    )
  );
